import { useState } from "react";
import { format } from "date-fns";

import { makePostApiCall } from "../shared/makeApiCall";
import { alignCalendarWeekToSunday } from "../utils/calendar/alignCalendarWeek";

const DEFAULT_TZ = "America/New_York";

function resolveTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TZ;
  } catch {
    return DEFAULT_TZ;
  }
}

export function useMonthCalendar(endpoint, logout, onError) {
  const [calendar, setCalendar] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(false);

  const getCalendar = async function (month = currentMonth, extraPayload = {}) {
    const formattedMonth = format(month, "yyyy-MM");
    setLoading(true);

    const callResult = await makePostApiCall(endpoint, {
      month: formattedMonth,
      tz: resolveTimeZone(),
      ...extraPayload,
    });

    setLoading(false);

    if (callResult.success) {
      const days = alignCalendarWeekToSunday(callResult.data ?? [], month);
      setCalendar(days);
      setSelectedDay(
        days.find((day) => day.isSelected) ??
          days.find((day) => day.isToday) ??
          null,
      );
      return;
    }

    if (callResult.errorStatus === 401) {
      logout();
      return;
    }

    const message =
      callResult.data?.message ||
      callResult.data?.subtitle ||
      "Could not load calendar.";
    onError?.(message);
  };

  const handleDaySelect = (selectedDate) => {
    const updatedDays = calendar.map((day) => ({
      ...day,
      isSelected: day.date === selectedDate,
    }));

    setCalendar(updatedDays);
    setSelectedDay(
      updatedDays.find((day) => day.date === selectedDate) ?? null,
    );
  };

  const onChangeMonth = (newMonth) => {
    setCurrentMonth(newMonth);
    getCalendar(newMonth);
  };

  return {
    calendar,
    currentMonth,
    selectedDay,
    loading,
    getCalendar,
    handleDaySelect,
    onChangeMonth,
  };
}
